import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import successAnimation from "../../assets/success.json";
import ReactConfetti from "react-confetti";
import Lottie from "lottie-react";
import { useWindowSize } from "react-use";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const { appointmentId, paymenttype, venderid, payMode } = location.state || {};
  const API_URL = process.env.REACT_APP_API_URL;

  const hasFetched = useRef(false);
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appointmentId || hasFetched.current) return;
    hasFetched.current = true;

    const fetchAndSubmitPaymentForm = async () => {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");

      try {
        let response;
        if (paymenttype === "Order") {
          response = await fetch(`${API_URL}orders/order-payment/${appointmentId}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              tenantId: tenantId,
            },
          });
        } else {
          response = await fetch(
            `${API_URL}orders/appointment-payment?appointmentid=${appointmentId}&payMode=${payMode}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                tenantId: tenantId,
              },
            }
          );
        }

        const result = await response.json();

        if (payMode === "CASH") {
          if (result.status === 200) {
            setPaymentStatus(true);
            setRemarks("Payment marked as successful.");
          } else {
            setRemarks("Cash payment failed or was not recorded.");
          }
          setLoading(false);
        } else {
          // Online payment mode (e.g., "CC")
          const container = document.createElement("div");
          container.innerHTML = result?.data || "";
          const form = container.querySelector("form");

          if (form) {
            document.body.appendChild(form);
            form.submit(); // redirects to payment gateway
          } else {
            console.error("No form found in payment gateway response.");
            setRemarks("Online payment failed: Invalid payment form.");
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("Payment fetch error:", err);
        setRemarks("Something went wrong during payment processing.");
        setLoading(false);
      }
    };

    fetchAndSubmitPaymentForm();
  }, [appointmentId, API_URL, payMode, paymenttype]);

  const baseContainer =
    "flex justify-center items-center h-screen relative overflow-hidden";
  const baseCard =
    "relative p-1 rounded-2xl shadow-2xl bg-white max-w-sm w-full text-center animate-fadeIn";

  const renderCard = (bgGradient, animationData, title, titleColor, btnColor) => (
    <div className={`${baseContainer} ${bgGradient}`}>
      <div className={baseCard}>
        <div className="relative z-10 p-8">
          <Lottie
            animationData={animationData}
            autoplay
            loop={false}
            style={{ height: 150 }}
          />
          <h1 className={`text-3xl font-bold ${titleColor} mt-4`}>{title}</h1>
          {remarks && <p className="text-gray-500 font-semibold mt-2 text-sm">{remarks}</p>}
          <button
            className={`mt-6 px-6 py-2 text-white font-semibold rounded-md shadow transition-all ${btnColor} hover:scale-105`}
            onClick={() => navigate("/dashboard")}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  // ⛔ Prevent rendering before decision
  if (loading) return null;

  // ✅ Show payment success screen
  if (paymentStatus) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-[#004276] to-[#3cba84] relative overflow-hidden">
        <ReactConfetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={250}
          colors={["#22c55e", "#16a34a", "#4ade80"]}
        />
        {renderCard(
          "",
          successAnimation,
          "Payment Processed Successfully!",
          "text-[#004276]",
          "bg-gradient-to-r from-[#004172] to-[#3cba84]"
        )}
      </div>
    );
  }

  // ⚠️ Show fallback UI if something failed
  return (
    <div className="flex justify-center items-center h-screen bg-gray-100">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-sm text-center">
        <h1 className="text-xl font-bold text-red-500">Payment Failed. Please try again or use an alternate payment method to complete the transaction.</h1>
        <p className="mt-2 text-gray-600 text-sm">{remarks || "Something went wrong."}</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-6 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
